<template>
  <div v-if="modelValue" class="dialog-overlay" @click.self="closable ? handleClose : () => {}">
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 class="dialog-title">{{ dialogTitle }}</h2>
        <button v-if="closable" class="dialog-close" @click="handleClose" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      
      <div class="dialog-content">
        <p class="dialog-message">{{ dialogMessage }}</p>
        
        <div class="dialog-benefits" v-if="showBenefits">
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Access detailed cashflow schedules</span>
          </div>
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Download diligence documents</span>
          </div>
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Invest securely in bonds</span>
          </div>
        </div>
      </div>
      
      <div class="dialog-actions">
        <button 
          class="dialog-button dialog-button--primary"
          @click="handleSignupClick"
        >
          {{ actionButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useUtmTracking } from '~/composables/useUtmTracking'

defineOptions({ name: 'SignupEnforcementDialog' })

interface Props {
  modelValue: boolean
  type?: 'signup' | 'login' | 'both'
  title?: string
  message?: string
  showBenefits?: boolean
  ctaName?: string
  signupUrl?: string
  closable?: boolean // When false, dialog cannot be closed (forced signup)
}

const props = withDefaults(defineProps<Props>(), {
  type: 'signup',
  title: '',
  message: '',
  showBenefits: true,
  closable: true
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const route = useRoute()
const { buildRedirectUrl } = useUtmTracking()
const { investorUrl } = useInvestorPlatform()

const dialogTitle = computed(() => {
  if (props.title) return props.title
  
  if (props.type === 'login') {
    return 'Sign In Required'
  } else if (props.type === 'both') {
    return 'Sign Up to View or Download Document'
  }
  return 'Sign Up Required'
})

const dialogMessage = computed(() => {
  if (props.message) return props.message
  
  if (props.type === 'login') {
    return 'Please sign in to access this feature.'
  } else if (props.type === 'both') {
    return 'Sign up to view or download documents.'
  }
  return 'Please sign up to access this feature and unlock exclusive investment opportunities.'
})

const actionButtonText = computed(() => {
  if (props.type === 'login') {
    return 'Sign In'
  }
  // For both 'signup' and 'both' types, use 'Signup'
  return 'Signup Now'
})

const signupUrl = computed(() => {
  if (props.signupUrl) return props.signupUrl

  // Always use /login as base URL for redirection
  const baseUrl = investorUrl('/login')
  
  // Use custom CTA name if provided, otherwise use default based on type
  const ctaName = props.ctaName || `signup-dialog-${props.type}`
  
  return buildRedirectUrl(baseUrl, route.path, ctaName)
})

const handleClose = () => {
  emit('update:modelValue', false)
}

if (typeof window !== "undefined") {
  window.addEventListener('close-all-overlays', () => {
    if (props.closable) {
      handleClose();
    }
  });
}

const handleSignupClick = () => {
  // Redirect to invest.equirize.com/login with UTM tracking
  window.open(signupUrl.value, '_blank');
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-container {
  background: #ffffff;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #EAF7FF;
}

.dialog-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 700;
  color: #1b2559;
  margin: 0;
  line-height: 28px;
}

.dialog-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #A3AED0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  border-radius: 4px;
}

.dialog-close:hover {
  color: #1b2559;
  background: #F9FBFF;
}

.dialog-content {
  padding: 24px;
}

.dialog-message {
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 400;
  color: #1b2559;
  line-height: 24px;
  margin: 0 0 20px;
}

.dialog-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background: #F9FBFF;
  border-radius: 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  color: #1b2559;
  line-height: 20px;
}

.benefit-item svg {
  flex-shrink: 0;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #EAF7FF;
  justify-content: center;
}

.dialog-button {
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  border: none;
}

.dialog-button--secondary {
  background: #F9FBFF;
  color: #1b2559;
  border: 1px solid #EAF7FF;
}

.dialog-button--secondary:hover {
  background: #EAF7FF;
}

.dialog-button--primary {
  background: #0027AF;
  color: #ffffff;
}

.dialog-button--primary:hover {
  background: #001f8a;
}

.dialog-button--primary:active {
  background: #001a66;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .dialog-overlay {
    padding: 16px;
  }

  .dialog-container {
    max-width: 100%;
  }

  .dialog-header {
    padding: 20px 20px 16px;
  }

  .dialog-title {
    font-size: 18px;
  }

  .dialog-content {
    padding: 20px;
  }

  .dialog-message {
    font-size: 15px;
  }

  .dialog-actions {
    flex-direction: column;
    padding: 16px 20px 20px;
  }

  .dialog-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: 16px 16px 12px;
  }

  .dialog-title {
    font-size: 16px;
  }

  .dialog-content {
    padding: 16px;
  }

  .dialog-message {
    font-size: 14px;
  }

  .dialog-benefits {
    padding: 12px;
  }

  .benefit-item {
    font-size: 13px;
  }

  .dialog-actions {
    padding: 12px 16px 16px;
  }
}
</style>
